import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api';
import { useLang } from '../context/LangContext';
import StatusBadge from '../components/StatusBadge';
import ScopeSelector from '../components/ScopeSelector';
import { downloadReport } from '../reportDownload';
import SendReportMailDialog from '../components/SendReportMailDialog';
import AuditeeMultiSelect from '../components/AuditeeMultiSelect';
import ReportPreview from '../components/ReportPreview';
import MaturityRadar from '../components/MaturityRadar';
import { getActiveSession, setActiveSession, loadPlanSessions, sessionFindings, sessionNow } from '../planSession';

function ScopeModal({ auditId, frameworkId, defaultQuestionCount = 10, onClose, onSave, t }) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  const [questionCount, setQuestionCount] = useState(defaultQuestionCount);
  const [genProgress, setGenProgress] = useState(null); // null | { current, total, errors }

  // Laad huidige scope
  useEffect(() => {
    api.get(`/api/audits/${auditId}/scope`).then(r => {
      setSelectedIds(new Set(r.data.selected_control_ids));
    }).finally(() => setLoading(false));
  }, [auditId]);

  async function handleSave() {
    if (selectedIds.size === 0) {
      setError('Selecteer minstens één control.');
      return;
    }
    setSaving(true); setError('');
    try {
      const res = await api.put(`/api/audits/${auditId}/scope`, {
        control_ids: [...selectedIds],
      });
      // Auditvragen genereren voor de nieuw toegevoegde controls (per item)
      const addedIds = res.data.added_finding_ids || [];
      let qErrors = 0;
      if (addedIds.length > 0 && questionCount > 0) {
        const total = addedIds.length;
        let done = 0;
        for (const fid of addedIds) {
          setGenProgress({ current: done, total, errors: qErrors });
          try {
            await api.post(`/api/audits/${auditId}/generate-questions`, {
              finding_ids: [fid],
              question_count: questionCount,
            });
          } catch (err) {
            console.warn('Auditvragen mislukt voor finding', fid, err);
            qErrors++;
          }
          done++;
          setGenProgress({ current: done, total, errors: qErrors });
        }
      }
      onSave({ ...res.data, questions_generated: addedIds.length - qErrors, questions_errors: qErrors });
    } catch (err) {
      setError(err.response?.data?.error || t('error'));
    } finally {
      setSaving(false);
      setGenProgress(null);
    }
  }

  return (
    <>
      <div className="modal show d-block" tabIndex="-1">
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h5 className="modal-title mb-0">Scope wijzigen</h5>
                <div className="text-muted small mt-1">
                  Vink controls aan of af — verwijderde controls verliezen hun bevindingen.
                </div>
              </div>
              <button type="button" className="btn-close" onClick={onClose} disabled={saving} />
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}
              {genProgress && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between small text-muted mb-1">
                    <span>
                      <span className="spinner-border spinner-border-sm me-2" style={{ width: 12, height: 12 }} />
                      Auditvragen genereren voor nieuwe controls…
                      {genProgress.errors > 0 && (
                        <span className="text-danger ms-2">{genProgress.errors} fout</span>
                      )}
                    </span>
                    <span>{genProgress.current} / {genProgress.total}</span>
                  </div>
                  <div className="progress" style={{ height: 6 }}>
                    <div className="progress-bar progress-bar-striped progress-bar-animated"
                      style={{ width: `${Math.round((genProgress.current / genProgress.total) * 100)}%` }} />
                  </div>
                </div>
              )}
              {loading ? (
                <div className="text-center text-muted py-4">
                  <div className="spinner-border spinner-border-sm me-2" />{t('loading')}
                </div>
              ) : (
                <ScopeSelector
                  frameworkId={frameworkId}
                  selectedIds={selectedIds}
                  setSelectedIds={setSelectedIds}
                  defaultSelectAll={false}
                />
              )}
              {!loading && (
                <div className="d-flex align-items-center gap-2 mt-3 pt-3 border-top">
                  <label className="form-label small mb-0" htmlFor="scopeQCount">
                    Auditvragen per nieuw toegevoegde control:
                  </label>
                  <input id="scopeQCount" type="number" min="0" max="20"
                    className="form-control form-control-sm" style={{ width: 80 }}
                    value={questionCount}
                    onChange={e => setQuestionCount(Math.max(0, Math.min(20, parseInt(e.target.value) || 0)))} />
                  <span className="text-muted small">0 = geen vragen genereren</span>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>{t('cancel')}</button>
              <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving || loading}>
                {saving && <span className="spinner-border spinner-border-sm me-2" />}
                {genProgress
                  ? `Vragen genereren ${genProgress.current}/${genProgress.total}…`
                  : `Scope opslaan (${selectedIds.size} controls)`}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop show" />
    </>
  );
}

function ShareModal({ auditId, clientId, onClose, t }) {
  const [contacts, setContacts] = useState([]);
  const [shares, setShares]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [form, setForm]         = useState({ contact_id: '', expires_days: 30, access_code: '', send: true });
  const [creating, setCreating] = useState(false);
  const [created, setCreated]   = useState(null);
  const [error, setError]       = useState('');

  const loadShares = () => api.get(`/api/audits/${auditId}/shares`).then(r => setShares(r.data));

  useEffect(() => {
    Promise.all([
      clientId ? api.get(`/api/clients/${clientId}/contacts`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      api.get(`/api/audits/${auditId}/shares`),
    ]).then(([c, s]) => { setContacts(c.data); setShares(s.data); }).finally(() => setLoading(false));
  }, [auditId, clientId]);

  async function handleCreate(e) {
    e.preventDefault(); setError(''); setCreating(true); setCreated(null);
    try {
      const res = await api.post(`/api/audits/${auditId}/shares`, {
        contact_id: form.contact_id || null,
        expires_days: form.expires_days,
        access_code: form.access_code || null,
        send: form.send && !!form.contact_id,
      });
      setCreated(res.data);
      await loadShares();
    } catch (err) { setError(err.response?.data?.error || t('error')); }
    finally { setCreating(false); }
  }

  async function handleRevoke(s) {
    if (!window.confirm(t('share_revoke_confirm'))) return;
    await api.delete(`/api/audits/${auditId}/shares/${s.id}`).catch(() => {});
    loadShares();
  }
  async function handleSend(s) {
    try { await api.post(`/api/audits/${auditId}/shares/${s.id}/send`); alert(t('share_sent')); }
    catch (err) { alert(err.response?.data?.error || t('error')); }
  }
  function copy(link) { if (navigator.clipboard) navigator.clipboard.writeText(link); }

  function shareStatus(s) {
    if (s.revoked) return t('share_st_revoked');
    if (s.expires_at && new Date(s.expires_at) < new Date()) return t('share_st_expired');
    return t('share_st_active');
  }

  return (
    <>
      <div className="modal show d-block" tabIndex="-1">
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title mb-0">{t('share_title')}</h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2">{error}</div>}

              <form onSubmit={handleCreate} className="border rounded p-3 mb-3">
                <div className="row g-2 align-items-end">
                  <div className="col-12 col-md-5">
                    <label className="form-label small fw-medium">{t('share_contact')}</label>
                    <select className="form-select form-select-sm" value={form.contact_id}
                      onChange={e => setForm(f => ({ ...f, contact_id: e.target.value }))}>
                      <option value="">{t('share_no_contact')}</option>
                      {contacts.map(c => <option key={c.id} value={c.id}>{c.name}{c.email ? ` (${c.email})` : ''}</option>)}
                    </select>
                  </div>
                  <div className="col-6 col-md-3">
                    <label className="form-label small fw-medium">{t('share_expires_days')}</label>
                    <input type="number" min="0" className="form-control form-control-sm" value={form.expires_days}
                      onChange={e => setForm(f => ({ ...f, expires_days: e.target.value }))} />
                  </div>
                  <div className="col-6 col-md-4">
                    <label className="form-label small fw-medium">{t('share_code')}</label>
                    <input className="form-control form-control-sm" value={form.access_code}
                      placeholder={t('share_code_ph')}
                      onChange={e => setForm(f => ({ ...f, access_code: e.target.value }))} />
                  </div>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="shareSend"
                      checked={form.send} disabled={!form.contact_id}
                      onChange={e => setForm(f => ({ ...f, send: e.target.checked }))} />
                    <label className="form-check-label small" htmlFor="shareSend">{t('share_send_email')}</label>
                  </div>
                  <button className="btn btn-primary btn-sm" disabled={creating}>
                    {creating && <span className="spinner-border spinner-border-sm me-2" />}{t('share_create')}
                  </button>
                </div>
              </form>

              {created && (
                <div className="alert alert-success">
                  <div className="small mb-2">{t('share_created')}{created.emailed ? ` · ${t('share_emailed')}` : ''}</div>
                  <div className="d-flex gap-2 align-items-center mb-2">
                    <input className="form-control form-control-sm" readOnly value={created.link} />
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => copy(created.link)}>{t('share_copy')}</button>
                  </div>
                  <img src={created.qr} alt="QR" style={{ width: 140, height: 140 }} className="border rounded bg-white p-1" />
                </div>
              )}

              <h6 className="small text-muted">{t('share_existing')}</h6>
              {loading ? (
                <div className="text-muted small"><span className="spinner-border spinner-border-sm me-2" />{t('loading')}</div>
              ) : shares.length === 0 ? (
                <div className="text-muted small">{t('share_none')}</div>
              ) : (
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>{t('share_contact')}</th>
                      <th>{t('bgj_col_status')}</th>
                      <th>{t('share_viewed')}</th>
                      <th className="text-end"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {shares.map(s => (
                      <tr key={s.id} className={s.revoked ? 'text-muted' : ''}>
                        <td className="small">
                          {s.contact_name || t('share_no_contact')}
                          {s.has_code && <i className="bi bi-lock-fill ms-1" title={t('share_code')} />}
                        </td>
                        <td className="small">{shareStatus(s)}</td>
                        <td className="small text-muted">{s.last_viewed_at ? new Date(s.last_viewed_at).toLocaleString() : '—'}</td>
                        <td className="text-end">
                          {!s.revoked && (
                            <>
                              <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => copy(s.link)} title={t('share_copy')}>
                                <i className="bi bi-clipboard" />
                              </button>
                              {s.contact_email && (
                                <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => handleSend(s)} title={t('share_send_email')}>
                                  <i className="bi bi-envelope" />
                                </button>
                              )}
                              <button className="btn btn-sm btn-outline-danger" onClick={() => handleRevoke(s)} title={t('share_revoke')}>
                                <i className="bi bi-x-circle" />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>{t('close')}</button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop show" />
    </>
  );
}

function VersionsModal({ auditId, onClose, onRestored, t }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [restoringId, setRestoringId] = useState(null);

  useEffect(() => {
    api.get(`/api/audits/${auditId}/versions`)
      .then(r => setVersions(r.data))
      .finally(() => setLoading(false));
  }, [auditId]);

  async function handleRestore(v) {
    if (!window.confirm(t('ver_restore_confirm').replace('{v}', v.version))) return;
    setRestoringId(v.id);
    try {
      await api.post(`/api/audits/${auditId}/versions/${v.id}/restore`);
      onRestored(v.version);
    } catch {
      alert(t('error'));
    } finally {
      setRestoringId(null);
    }
  }

  return (
    <>
      <div className="modal show d-block" tabIndex="-1">
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title mb-0">{t('ver_title')}</h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              {loading ? (
                <div className="text-center text-muted py-4"><div className="spinner-border spinner-border-sm me-2" />{t('loading')}</div>
              ) : versions.length === 0 ? (
                <div className="text-muted text-center py-4">{t('ver_none')}</div>
              ) : (
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>{t('ver_version')}</th>
                      <th>{t('ver_label')}</th>
                      <th>{t('ver_date')}</th>
                      <th>{t('ver_by')}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {versions.map(v => (
                      <tr key={v.id}>
                        <td className="fw-semibold">v{v.version}</td>
                        <td>
                          {v.label === 'gepubliceerd'
                            ? <span className="badge badge-conform">{t('ver_published')}</span>
                            : <span className="text-muted small">{v.label}</span>}
                        </td>
                        <td className="small text-muted">{new Date(v.created_at).toLocaleString()}</td>
                        <td className="small">{v.created_by_name || '—'}</td>
                        <td className="text-end">
                          <button className="btn btn-sm btn-outline-primary"
                            onClick={() => handleRestore(v)} disabled={restoringId === v.id}>
                            {restoringId === v.id
                              ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} />
                              : <><i className="bi bi-arrow-counterclockwise me-1" />{t('ver_restore')}</>}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>{t('close')}</button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop show" />
    </>
  );
}

const COLS_PREF = 'audit_controls_cols';   // sleutel in user_preferences

// Standaardbreedtes (px) van de controltabel; de kolom Naam neemt de rest
const DEFAULT_COL_WIDTHS = {
  section: 70, control: 90, status: 80, priority: 90, due_date: 110, assigned: 140, maturity: 130, notes: 200,
};

// Kolomrand verslepen zoals in Excel: enkel de versleepte rand beweegt, de twee
// kolommen ernaast ruilen breedte en de rest blijft staan. 'name' (Naam) heeft
// geen vaste breedte en vult de rest. onChange(key, px) zet een breedte.
function dragBorder(e, leftKey, rightKey, onChange) {
  e.preventDefault();
  e.stopPropagation();
  const thL = e.currentTarget.parentElement;
  const thR = thL.nextElementSibling;
  const startX = e.clientX;
  const wL = thL.offsetWidth;
  const wR = thR.offsetWidth;
  const MIN = 40;
  const move = (ev) => {
    const dx = Math.min(Math.max(ev.clientX - startX, MIN - wL), wR - MIN);
    if (leftKey !== 'name')  onChange(leftKey,  Math.round(wL + dx));
    if (rightKey !== 'name') onChange(rightKey, Math.round(wR - dx));
  };
  const up = () => {
    document.removeEventListener('pointermove', move);
    document.removeEventListener('pointerup', up);
    document.body.classList.remove('col-resizing');
  };
  document.addEventListener('pointermove', move);
  document.addEventListener('pointerup', up);
  document.body.classList.add('col-resizing');
}

// Kolomkop met een sleepgreep op de rechterrand (onDrag), dubbelklik = standaard
function ResizableTh({ width, onDrag, onReset, children }) {
  return (
    <th style={{ width, position: 'relative' }}>
      {children}
      {onDrag && (
        <span className="col-resizer" onPointerDown={onDrag} onClick={e => e.stopPropagation()}
          onDoubleClick={onReset} />
      )}
    </th>
  );
}

// Sluit een open menu bij een klik erbuiten
function useClickOutside(ref, open, close) {
  useEffect(() => {
    if (!open) return;
    function onDown(e) {
      if (ref.current && !ref.current.contains(e.target)) close();
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [ref, open, close]);
}

const FINDING_STATUSES = ['niet_beoordeeld', 'conform', 'niet_conform', 'observatie', 'kans_verbetering', 'nvt'];
const MATURITY_LABELS = ['Onvolledig', 'Initieel', 'Beheerd', 'Gedefinieerd', 'Kwantitatief beheerd', 'Optimaliserend'];

const STATUS_CONFIG = {
  niet_beoordeeld:  { label: '—',   textColor: '#6b7280', bg: '#f1f5f9' },
  conform:          { label: 'C',   textColor: '#1d4ed8', bg: '#dbeafe' },
  niet_conform:     { label: 'NC',  textColor: '#b91c1c', bg: '#fee2e2' },
  observatie:       { label: 'OBS', textColor: '#c2410c', bg: '#ffedd5' },
  kans_verbetering: { label: 'OFI', textColor: '#15803d', bg: '#dcfce7' },
  nvt:              { label: 'N/A', textColor: '#6b7280', bg: '#f1f5f9' },
};

function StatusPicker({ status, onChange }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.niet_beoordeeld;

  useEffect(() => {
    if (!open) return;
    function onDown(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <span
        role="button"
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          minWidth: 48, padding: '3px 8px', borderRadius: 6, cursor: 'pointer',
          background: cfg.bg, color: cfg.textColor,
          fontWeight: 700, fontSize: 12, userSelect: 'none',
          border: `1.5px solid ${cfg.textColor}33`,
        }}
      >
        {cfg.label}
      </span>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 1050,
          background: '#fff', borderRadius: 8,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          padding: 4, minWidth: 170,
        }}>
          {FINDING_STATUSES.map(s => {
            const c = STATUS_CONFIG[s];
            return (
              <div
                key={s}
                role="button"
                onClick={() => { onChange(s); setOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '5px 8px', borderRadius: 4, cursor: 'pointer',
                  background: status === s ? '#f1f5f9' : 'transparent',
                  fontWeight: status === s ? 600 : 400,
                }}
                onMouseEnter={e => { if (status !== s) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => { if (status !== s) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 38, padding: '1px 4px', borderRadius: 4, flexShrink: 0,
                  background: c.bg, color: c.textColor, fontWeight: 700, fontSize: 11,
                }}>
                  {c.label}
                </span>
                <span style={{ fontSize: 13 }}>{t(`fs_${s}`)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AuditDetailPage() {
  const { id } = useParams();
  const { t } = useLang();
  const navigate = useNavigate();

  const [audit, setAudit]       = useState(null);
  const [findings, setFindings] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [section, setSection]   = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeSession, setActiveSessionState] = useState(() => getActiveSession(id));
  const [planSessions, setPlanSessions]   = useState([]);
  const [sessionTopicsOpen, setSessionTopicsOpen] = useState(true);

  const [polishOpen, setPolishOpen]         = useState(false);
  const [polishLang, setPolishLang]         = useState('nl');
  const [polishFields, setPolishFields]     = useState({ situation: true, finding: true, actions: true });
  const [polishActionCount, setPolishActionCount] = useState(3);
  const [polishOverwrite, setPolishOverwrite] = useState(true);
  const [polishing, setPolishing]           = useState(false);
  const [polishResult, setPolishResult]     = useState(null);
  const [polishProgress, setPolishProgress] = useState(null);
  const pollRef = useRef(null);
  const [pdfLoading,     setPdfLoading]     = useState(false);
  const [wordLoading,    setWordLoading]    = useState(false);
  const [pdfPreviewUrl,  setPdfPreviewUrl]  = useState(null);
  const [reportOptsOpen, setReportOptsOpen]     = useState(false);
  const [reportOpts,     setReportOpts]         = useState({
    situation: true, finding: true, recommendation: true, priority: true, due_date: true,
  });
  const reportOptsRef = useRef(null);
  const [savedReports,  setSavedReports]  = useState([]);
  const [savingFormat,  setSavingFormat]  = useState(null);
  const [hasTemplate,   setHasTemplate]   = useState(false);
  const [mailReport,    setMailReport]    = useState(null);
  const [previewReport, setPreviewReport] = useState(null);
  const [auditeeContacts, setAuditeeContacts] = useState([]);
  const [auditeeIds,      setAuditeeIds]      = useState([]);
  const [maturity,        setMaturity]        = useState(null);


  // Column settings
  const [colMenu, setColMenu]                         = useState(null);   // { x, y } van het kolommenmenu
  const [showNotes, setShowNotes]                     = useState(true);
  const [showFindingPreview, setShowFindingPreview]   = useState(true);
  const [showSection, setShowSection]                 = useState(false);
  const [showPriority, setShowPriority]               = useState(false);
  const [showDueDate, setShowDueDate]                 = useState(false);
  const [showAssigned, setShowAssigned]               = useState(false);
  const [showMaturity, setShowMaturity]               = useState(false);
  const [colWidths, setColWidths]                     = useState({});     // eigen breedtes; ontbreekt → standaard
  const colMenuRef = useRef(null);
  const colWidth = (key) => colWidths[key] ?? DEFAULT_COL_WIDTHS[key];
  const setColWidth = (key, w) => setColWidths(ws => {
    const next = { ...ws };
    if (w == null) delete next[key]; else next[key] = w;
    return next;
  });

  // Kolominstellingen per gebruiker op de server. Oude instellingen uit de
  // browser (localStorage) worden eenmalig overgenomen.
  const [colsLoaded, setColsLoaded] = useState(false);
  useEffect(() => {
    const apply = (s) => {
      if (!s) return;
      if (s.showNotes         !== undefined) setShowNotes(s.showNotes);
      if (s.showFindingPreview!== undefined) setShowFindingPreview(s.showFindingPreview);
      if (s.showSection       !== undefined) setShowSection(s.showSection);
      if (s.showPriority      !== undefined) setShowPriority(s.showPriority);
      if (s.showDueDate       !== undefined) setShowDueDate(s.showDueDate);
      if (s.showAssigned      !== undefined) setShowAssigned(s.showAssigned);
      if (s.showMaturity      !== undefined) setShowMaturity(s.showMaturity);
      if (s.colWidths) setColWidths(s.colWidths);
    };
    api.get(`/api/users/me/preferences/${COLS_PREF}`)
      .then(r => {
        if (r.data.value) return apply(r.data.value);
        try { apply(JSON.parse(localStorage.getItem('auditControlsCols') || 'null')); } catch { /* niets bewaard */ }
      })
      .catch(() => {})
      .finally(() => setColsLoaded(true));
  }, []);
  // Bewaren met vertraging: tijdens het slepen van een kolomrand niet bij elke pixel
  useEffect(() => {
    if (!colsLoaded) return;
    const timer = setTimeout(() => {
      api.put(`/api/users/me/preferences/${COLS_PREF}`, { value: {
        showNotes, showFindingPreview, showSection, showPriority, showDueDate, showAssigned, showMaturity, colWidths,
      } }).then(() => { try { localStorage.removeItem('auditControlsCols'); } catch { /* geen opslag */ } })
        .catch(() => {});
    }, 600);
    return () => clearTimeout(timer);
  }, [colsLoaded, showNotes, showFindingPreview, showSection, showPriority, showDueDate, showAssigned, showMaturity, colWidths]);

  const [scopeOpen, setScopeOpen]     = useState(false);
  const [scopeResult, setScopeResult] = useState(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [shareOpen, setShareOpen]       = useState(false);
  const [moreOpen, setMoreOpen]         = useState(false);
  const moreRef = useRef(null);

  useClickOutside(colMenuRef, !!colMenu, () => setColMenu(null));
  useClickOutside(moreRef, moreOpen, () => setMoreOpen(false));

  // Kolommenmenu: rechtsklik op de tabelkop, of het icoon in de laatste kolom
  function openColMenu(e) {
    e.preventDefault();
    const MENU_W = 240;
    if (e.type === 'contextmenu') setColMenu({ x: Math.min(e.clientX, window.innerWidth - MENU_W - 8), y: e.clientY });
    else {
      const r = e.currentTarget.getBoundingClientRect();
      setColMenu({ x: r.right - MENU_W, y: r.bottom + 4 });
    }
  }

  useEffect(() => {
    if (!reportOptsOpen) return;
    function onDown(e) {
      if (reportOptsRef.current && !reportOptsRef.current.contains(e.target)) setReportOptsOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [reportOptsOpen]);

  const load = useCallback(() => {
    Promise.all([
      api.get(`/api/audits/${id}`),
      api.get(`/api/audits/${id}/findings`),
    ]).then(([a, f]) => {
      setAudit(a.data);
      setFindings(f.data);
      setPolishLang(a.data.language || 'nl');
    }).finally(() => setLoading(false));
  }, [id]);

  useEffect(load, [load]);

  const loadReports = useCallback(() => {
    api.get(`/api/reports/audit/${id}`).then(r => setSavedReports(r.data)).catch(() => {});
  }, [id]);
  useEffect(loadReports, [loadReports]);

  // Is er een eigen sjabloon actief voor het framework van deze audit?
  useEffect(() => {
    if (!audit?.framework_id) return;
    api.get('/api/templates')
      .then(r => setHasTemplate(r.data.some(tpl => tpl.kind === 'report' && tpl.is_active && String(tpl.framework_id) === String(audit.framework_id))))
      .catch(() => {});
  }, [audit?.framework_id]);

  // Auditees (algemeen): klantcontacten + huidige selectie laden
  useEffect(() => {
    if (!audit?.client_id) return;
    api.get(`/api/clients/${audit.client_id}/contacts`).then(r => setAuditeeContacts(r.data)).catch(() => {});
    api.get(`/api/audits/${id}/auditees`).then(r => setAuditeeIds(r.data.map(a => a.id))).catch(() => {});
  }, [audit?.client_id, id]);

  function saveAuditees(ids) {
    setAuditeeIds(ids);
    api.put(`/api/audits/${id}/auditees`, { contact_ids: ids }).catch(() => {});
  }

  // Maturiteit per thema (B9) — alleen ISO 27001. Herlaadt met de findings mee.
  useEffect(() => {
    if (audit?.framework_code !== 'iso27001') { setMaturity(null); return; }
    api.get(`/api/audits/${id}/maturity`).then(r => setMaturity(r.data)).catch(() => {});
  }, [audit?.framework_code, id, findings]);

  // Sessiemodus: sessies uit het auditplan; de gekozen sessie bepaalt de lijst
  useEffect(() => {
    loadPlanSessions(id).then(setPlanSessions).catch(() => {});
  }, [id]);

  function chooseSession(sessionId) {
    const v = sessionId === 'all' ? null : sessionId;
    setActiveSessionState(v);
    setActiveSession(id, v);
  }

  const session    = planSessions.find(s => s.id === activeSession) || null;
  const nowSession = !activeSession ? sessionNow(planSessions) : null;
  const relatedCodes = new Set(session?.related || []);
  const plannedCodes = new Set(planSessions.flatMap(s => [...s.primary, ...s.related]));
  const baseList = session ? sessionFindings(session, findings)
    : activeSession === 'none' ? findings.filter(f => !plannedCodes.has(f.control_id))
    : findings;
  const sessionPrimary  = session ? findings.filter(f => session.primary.includes(f.control_id)) : [];
  const sessionAssessed = sessionPrimary.filter(f => f.status !== 'niet_beoordeeld').length;

  const sections = ['all', ...new Set(findings.map(f => f.section))];
  const visible  = baseList.filter(f => {
    const secOk  = section === 'all' || f.section === section;
    const statOk = filterStatus === 'all' || f.status === filterStatus;
    return secOk && statOk;
  });

  const total      = findings.length;
  const assessed   = findings.filter(f => f.status !== 'niet_beoordeeld').length;
  const conform    = findings.filter(f => f.status === 'conform').length;
  const nonConform = findings.filter(f => f.status === 'niet_conform').length;
  const observatie = findings.filter(f => f.status === 'observatie').length;
  const ofi        = findings.filter(f => f.status === 'kans_verbetering').length;
  const nvt        = findings.filter(f => f.status === 'nvt').length;

  // Segmenten voor de voortgangsbalk (volgorde + kleur uit STATUS_CONFIG.textColor)
  const progressSegments = [
    { status: 'conform',          count: conform },
    { status: 'niet_conform',     count: nonConform },
    { status: 'observatie',       count: observatie },
    { status: 'kans_verbetering', count: ofi },
    { status: 'nvt',              count: nvt },
  ];

  async function handleStatusChange(findingId, newStatus) {
    setFindings(prev => prev.map(f => f.id === findingId ? { ...f, status: newStatus } : f));
    await api.put(`/api/findings/${findingId}`, { status: newStatus });
  }

  const REPORT_FIELDS = [
    ['situation',      'rep_field_situation'],
    ['finding',        'rep_field_finding'],
    ['recommendation', 'rep_field_recommendation'],
    ['priority',       'rep_field_priority'],
    ['due_date',       'rep_field_due_date'],
  ];

  function reportBody() {
    return { fields: reportOpts };
  }

  async function handlePdf() {
    setPdfLoading(true);
    setReportOptsOpen(false);
    try {
      const res = await api.post(`/api/reports/${id}/pdf`, reportBody(), { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPdfPreviewUrl(url);
    } catch {
      alert(t('error'));
    } finally {
      setPdfLoading(false);
    }
  }

  function closePdfPreview() {
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewUrl(null);
  }

  async function handleWord() {
    setWordLoading(true);
    setReportOptsOpen(false);
    try {
      const res = await api.post(`/api/reports/${id}/docx`, reportBody(), { responseType: 'blob' });
      const url = URL.createObjectURL(
        new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' })
      );
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-rapport-${id}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      alert(t('error'));
    } finally {
      setWordLoading(false);
    }
  }

  async function handleSaveReport(format) {
    setSavingFormat(format);
    setReportOptsOpen(false);
    try {
      await api.post(`/api/reports/${id}/save`, { format, ...reportBody() });
      loadReports();
      alert(t('rep_saved'));
    } catch (e) {
      alert(e.response?.data?.error || t('error'));
    } finally {
      setSavingFormat(null);
    }
  }

  async function handleDeleteReport(reportId) {
    if (!window.confirm(t('rep_delete_confirm'))) return;
    await api.delete(`/api/reports/${reportId}`);
    loadReports();
  }

  function startPolling() {
    if (pollRef.current) return; // al aan het pollen
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/api/audits/${id}/polish/status`);
        const job = res.data;
        if (!job.running && job.status !== 'running') {
          // Job klaar of geen job
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (job.status === 'done') {
            setPolishProgress(null);
            setPolishResult({ polished: job.polished, errors: job.errors });
            setPolishing(false);
            load();
          } else if (!job.running) {
            // Geen actieve job meer (bv. na server herstart)
            setPolishing(false);
            setPolishProgress(null);
          }
        } else {
          setPolishProgress({
            done:    job.done,
            total:   job.total,
            label:   job.current || '…',
            errors:  job.errors,
          });
          setPolishing(true);
        }
      } catch {
        // Poll fout — stop polling
        clearInterval(pollRef.current);
        pollRef.current = null;
        setPolishing(false);
      }
    }, 2000);
  }

  // Bij laden: check of er al een job loopt voor deze audit
  useEffect(() => {
    api.get(`/api/audits/${id}/polish/status`).then(res => {
      if (res.data.running) {
        setPolishing(true);
        setPolishLang(res.data.language || 'nl');
        setPolishProgress({
          done:   res.data.done,
          total:  res.data.total,
          label:  res.data.current || '…',
          errors: res.data.errors,
        });
        startPolling();
      }
    }).catch(() => {});
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handlePolish() {
    setPolishing(true);
    setPolishResult(null);
    setPolishProgress(null);
    try {
      const res = await api.post(`/api/audits/${id}/polish`, {
        language: polishLang,
        fields: polishFields,
        action_count: polishActionCount,
        overwrite: polishOverwrite,
      });
      if (res.status === 202) {
        setPolishProgress({ done: 0, total: res.data.total, label: '…', errors: 0 });
        startPolling();
      }
    } catch (err) {
      setPolishing(false);
      setPolishResult({ error: err.response?.data?.error || t('error') });
    }
  }

  async function handleStatusAudit(status) {
    await api.put(`/api/audits/${id}`, { ...audit, status });
    setAudit(a => ({ ...a, status }));
  }


  if (loading) return <div className="page-body"><div className="spinner-border spinner-border-sm" /></div>;

  // Zichtbare kolommen van de controltabel, in volgorde (zonder de pijlkolom)
  const tableCols = [
    showSection  && { key: 'section',  label: t('col_section') },
    { key: 'control', label: 'Control' },
    { key: 'name',    label: t('audit_name') },
    { key: 'status',  label: t('audit_status') },
    showPriority && { key: 'priority', label: t('finding_priority') },
    showDueDate  && { key: 'due_date', label: t('finding_due_date') },
    showAssigned && { key: 'assigned', label: t('col_assigned') },
    showMaturity && { key: 'maturity', label: t('maturity') },
    showNotes    && { key: 'notes',    label: t('finding_notes') },
  ].filter(Boolean);
  if (!audit)  return <div className="page-body text-danger">{t('error')}</div>;

  return (
    <>
      <div className="page-header">
        <div>
          <div className="text-muted-sm mb-1">
            <Link to="/audits" className="text-muted text-decoration-none">{t('nav_audits')}</Link>
            {' / '}
          </div>
          <h2 className="d-flex align-items-center gap-2">
            {audit.name}
            <button className="badge bg-light text-muted border fw-normal" style={{ cursor: 'pointer' }}
              onClick={() => setVersionsOpen(true)} title={t('ver_versions')}>
              v{audit.version_major}.{audit.version_minor}
            </button>
          </h2>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <Link className="btn btn-outline-secondary btn-sm" to={`/audits/${id}/plan`}>
            <i className="bi bi-calendar3 me-1" />{t('plan_title')}
          </Link>
          <button className="btn btn-outline-secondary btn-sm" onClick={() => setPolishOpen(o => !o)}>
            ✨ {t('finding_polish')}
          </button>
          {/* Rapport-dropdown */}
          <div className="position-relative" ref={reportOptsRef}>
            <button
              className="btn btn-outline-secondary btn-sm dropdown-toggle"
              onClick={() => setReportOptsOpen(o => !o)}
              disabled={pdfLoading || wordLoading}
            >
              <i className="bi bi-download me-1" />Rapport
            </button>
            {reportOptsOpen && (
              <div className="card shadow-sm position-absolute end-0 mt-1 p-3"
                style={{ zIndex: 200, minWidth: 260, top: '100%' }}>
                <div className="small fw-semibold mb-2">{t('rep_fields_title')}</div>

                {REPORT_FIELDS.map(([key, labelKey]) => (
                  <div className="form-check" key={key}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`chk-${key}`}
                      checked={reportOpts[key]}
                      onChange={e => setReportOpts(o => ({ ...o, [key]: e.target.checked }))}
                    />
                    <label className="form-check-label small" htmlFor={`chk-${key}`}>
                      {t(labelKey)}
                    </label>
                  </div>
                ))}
                <div className="text-muted mb-2 mt-2" style={{ fontSize: 11 }}>
                  <i className="bi bi-lock me-1" />{t('rep_notes_never')}
                </div>
                {hasTemplate && (
                  <div className="text-muted mb-3" style={{ fontSize: 11 }}>
                    <i className="bi bi-info-circle me-1" />{t('rep_template_hint')}
                  </div>
                )}

                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-outline-danger flex-fill"
                    onClick={handlePdf}
                    disabled={pdfLoading}
                  >
                    {pdfLoading
                      ? <span className="spinner-border spinner-border-sm" />
                      : <><i className="bi bi-file-pdf me-1" />PDF</>}
                  </button>
                  <button
                    className="btn btn-sm btn-outline-primary flex-fill"
                    onClick={handleWord}
                    disabled={wordLoading}
                  >
                    {wordLoading
                      ? <span className="spinner-border spinner-border-sm" />
                      : <><i className="bi bi-file-word me-1" />Word</>}
                  </button>
                </div>

                <hr className="my-2" />
                <div className="small fw-semibold mb-2">{t('rep_save')}</div>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-outline-secondary flex-fill"
                    onClick={() => handleSaveReport('pdf')}
                    disabled={savingFormat !== null}
                  >
                    {savingFormat === 'pdf'
                      ? <span className="spinner-border spinner-border-sm" />
                      : <><i className="bi bi-save me-1" />{t('rep_save_pdf')}</>}
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary flex-fill"
                    onClick={() => handleSaveReport('docx')}
                    disabled={savingFormat !== null}
                  >
                    {savingFormat === 'docx'
                      ? <span className="spinner-border spinner-border-sm" />
                      : <><i className="bi bi-save me-1" />{t('rep_save_word')}</>}
                  </button>
                </div>
              </div>
            )}
          </div>
          {/* Minder gebruikte acties */}
          <div className="position-relative" ref={moreRef}>
            <button className={`btn btn-sm ${moreOpen ? 'btn-secondary' : 'btn-outline-secondary'}`}
              onClick={() => setMoreOpen(o => !o)} title={t('more_actions')}>
              <i className="bi bi-three-dots" />
            </button>
            {moreOpen && (
              <div className="dropdown-menu show end-0 mt-1" style={{ top: '100%', right: 0, left: 'auto' }}>
                <button className="dropdown-item small" onClick={() => { setMoreOpen(false); setScopeResult(null); setScopeOpen(true); }}>
                  <i className="bi bi-funnel me-2" />Scope wijzigen
                </button>
                <button className="dropdown-item small" onClick={() => { setMoreOpen(false); setVersionsOpen(true); }}>
                  <i className="bi bi-clock-history me-2" />{t('ver_versions')}
                </button>
                <button className="dropdown-item small" onClick={() => { setMoreOpen(false); setShareOpen(true); }}>
                  <i className="bi bi-share me-2" />{t('share_title')}
                </button>
              </div>
            )}
          </div>
          {audit.status !== 'closed' && audit.status !== 'archived' && (
            <button className="btn btn-primary btn-sm" onClick={() => handleStatusAudit('closed')}>
              {t('audit_close')}
            </button>
          )}
        </div>
      </div>

      <div className="page-body">

        {/* Info + stats */}
        <div className="row g-3 mb-4">
          <div className="col-md-8">
            <div className="card h-100">
              <div className="card-body d-flex flex-wrap gap-3 align-items-center">
                <div><span className="text-muted small">{t('audit_client')}: </span><strong>{audit.client_name}</strong></div>
                <div><span className="text-muted small">{t('audit_framework')}: </span>{audit.framework_name}</div>
                <div><span className="text-muted small">{t('audit_auditor')}: </span>{audit.auditor_name}</div>
                <div><span className="text-muted small">{t('audit_date')}: </span>{audit.audit_date ? new Date(audit.audit_date).toLocaleDateString() : '—'}</div>
                <div><StatusBadge status={audit.status} /></div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card h-100">
              <div className="card-body">
                <div className="audit-stats mb-2">
                  <div>
                    <div style={{ fontSize: 20, fontWeight: 700 }}>{assessed}/{total}</div>
                    <div className="text-muted small">{t('assessed')}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: STATUS_CONFIG.conform.textColor }}>{conform}</div>
                    <div className="text-muted small">{t('fs_conform')}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: STATUS_CONFIG.niet_conform.textColor }}>{nonConform}</div>
                    <div className="text-muted small">{t('fs_niet_conform')}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: STATUS_CONFIG.observatie.textColor }}>{observatie}</div>
                    <div className="text-muted small">{t('fs_observatie')}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: STATUS_CONFIG.kans_verbetering.textColor }}>{ofi}</div>
                    <div className="text-muted small">{t('fs_kans_verbetering')}</div>
                  </div>
                </div>
                <div className="progress" title={`${assessed}/${total} beoordeeld`}>
                  {total > 0 && progressSegments.map(seg => seg.count > 0 && (
                    <div key={seg.status} className="progress-bar"
                      style={{ width: `${(seg.count / total) * 100}%`, background: STATUS_CONFIG[seg.status].textColor }}
                      title={`${t(`fs_${seg.status}`)}: ${seg.count}`} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Auditees (algemeen) */}
        <div className="card mb-4">
          <div className="card-body py-2 d-flex align-items-center gap-2 flex-wrap">
            <span className="text-muted small"><i className="bi bi-people me-1" />{t('auditees')}:</span>
            <AuditeeMultiSelect
              contacts={auditeeContacts}
              value={auditeeIds}
              onChange={saveAuditees}
              placeholder={t('auditees_none')}
              idPrefix="audit-auditee"
            />
            <span className="text-muted" style={{ fontSize: 11 }}>{t('auditees_general_hint')}</span>
          </div>
        </div>

        {/* Maturiteit per thema (B9) — altijd zichtbaar bij ISO 27001 */}
        {maturity?.applies && maturity.themes.length > 0 && (
          <div className="card mb-4">
            <div className="card-body">
              <div className="fw-semibold small mb-3">
                <i className="bi bi-diagram-3 me-1" />{t('maturity_per_theme')}
              </div>
              {!maturity.themes.some(th => th.avg != null) && (
                <div className="text-muted small mb-3">
                  <i className="bi bi-info-circle me-1" />{t('maturity_none_yet')}
                </div>
              )}
              <div className="row g-3 align-items-center">
                {maturity.themes.filter(th => th.avg != null).length >= 3 && (
                  <div className="col-md-auto text-center">
                    <MaturityRadar themes={maturity.themes} />
                  </div>
                )}
                <div className="col">
                  <table className="table table-sm mb-0" style={{ maxWidth: 420 }}>
                    <thead>
                      <tr>
                        <th>{t('col_section')}</th>
                        <th className="text-end">{t('maturity_avg')}</th>
                        <th className="text-end">{t('maturity_scored')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {maturity.themes.map(th => (
                        <tr key={th.section}>
                          <td className="fw-medium">{th.section}</td>
                          <td className="text-end">{th.avg != null ? Number(th.avg).toFixed(1) : '—'}</td>
                          <td className="text-end text-muted">{th.scored}/{th.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bewaarde rapporten */}
        {savedReports.length > 0 && (
          <div className="card mb-4">
            <div className="card-body">
              <div className="small fw-semibold mb-2">
                <i className="bi bi-file-earmark-bar-graph me-1" />{t('rep_section')}
              </div>
              <div className="table-responsive">
                <table className="table table-sm table-hover mb-0 align-middle">
                  <tbody>
                    {savedReports.map(r => (
                      <tr key={r.id}>
                        <td style={{ width: 80 }}>
                          <span className={`badge ${r.format === 'pdf' ? 'bg-danger' : 'bg-primary'}`}>
                            {r.format.toUpperCase()}
                          </span>
                        </td>
                        <td>v{r.version}</td>
                        <td className="text-muted small">
                          {r.include_recommendations ? t('rep_recs_yes') : t('rep_recs_no')}
                        </td>
                        <td className="text-muted small text-nowrap">
                          {new Date(r.created_at).toLocaleString()} · {r.created_by_name || '—'}
                        </td>
                        <td className="text-end" style={{ width: 170 }}>
                          <button className="btn btn-sm btn-outline-secondary me-1"
                            onClick={() => setPreviewReport(r)}
                            title={t('preview')}>
                            <i className="bi bi-eye" />
                          </button>
                          <button className="btn btn-sm btn-outline-primary me-1"
                            onClick={() => setMailReport(r)}
                            title={t('mail_send')}>
                            <i className="bi bi-envelope" />
                          </button>
                          <button className="btn btn-sm btn-outline-secondary me-1"
                            onClick={() => downloadReport(r.id, r.original_filename)}
                            title={t('rep_download')}>
                            <i className="bi bi-download" />
                          </button>
                          <button className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteReport(r.id)}
                            title={t('delete')}>
                            <i className="bi bi-trash" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Scope-resultaat banner */}
        {scopeResult && (
          <div className="alert alert-success alert-dismissible py-2 mb-4">
            Scope bijgewerkt: <strong>{scopeResult.added}</strong> control{scopeResult.added !== 1 ? 's' : ''} toegevoegd,{' '}
            <strong>{scopeResult.removed}</strong> verwijderd.
            {scopeResult.questions_generated > 0 && (
              <> Auditvragen gegenereerd voor <strong>{scopeResult.questions_generated}</strong> nieuwe control{scopeResult.questions_generated !== 1 ? 's' : ''}.</>
            )}
            {scopeResult.questions_errors > 0 && (
              <span className="text-danger"> {scopeResult.questions_errors} control(s) overgeslagen wegens fout.</span>
            )}
            <button type="button" className="btn-close" onClick={() => setScopeResult(null)} />
          </div>
        )}

        {/* Polish panel */}
        {polishOpen && (
          <div className="polish-panel mb-4">
            <h5 className="mb-3">✨ {t('polish_title')}</h5>
            <div className="text-muted small mb-3">
              Polisht notities naar <strong>huidige situatie</strong>, schrijft een <strong>bevinding</strong> op basis van de status, en stelt <strong>acties</strong> voor. Sloeg N.v.t. en Niet beoordeeld over.
            </div>

            <div className="d-flex flex-wrap gap-4 align-items-start mb-3">
              <div>
                <label className="form-label small fw-medium d-block mb-1">{t('polish_what')}</label>
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="pf-sit"
                    checked={polishFields.situation} disabled={polishing}
                    onChange={e => setPolishFields(f => ({ ...f, situation: e.target.checked }))} />
                  <label className="form-check-label small" htmlFor="pf-sit">{t('polish_f_situation')}</label>
                </div>
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="pf-find"
                    checked={polishFields.finding} disabled={polishing}
                    onChange={e => setPolishFields(f => ({ ...f, finding: e.target.checked }))} />
                  <label className="form-check-label small" htmlFor="pf-find">{t('polish_f_finding')}</label>
                </div>
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="pf-act"
                    checked={polishFields.actions} disabled={polishing}
                    onChange={e => setPolishFields(f => ({ ...f, actions: e.target.checked }))} />
                  <label className="form-check-label small" htmlFor="pf-act">{t('polish_f_actions')}</label>
                </div>
                {polishFields.actions && (
                  <div className="d-flex align-items-center gap-2 ms-4 mt-1">
                    <label className="small text-muted mb-0" htmlFor="pf-actcount">{t('polish_action_count')}</label>
                    <input id="pf-actcount" type="number" min="1" max="5" disabled={polishing}
                      className="form-control form-control-sm" style={{ width: 64 }}
                      value={polishActionCount}
                      onChange={e => setPolishActionCount(Math.max(1, Math.min(5, parseInt(e.target.value) || 1)))} />
                  </div>
                )}
              </div>
              <div>
                <label className="form-label small fw-medium d-block mb-1">{t('polish_mode')}</label>
                <div className="form-check">
                  <input className="form-check-input" type="radio" name="polishMode" id="pm-over"
                    checked={polishOverwrite} disabled={polishing}
                    onChange={() => setPolishOverwrite(true)} />
                  <label className="form-check-label small" htmlFor="pm-over">{t('polish_overwrite')}</label>
                </div>
                <div className="form-check">
                  <input className="form-check-input" type="radio" name="polishMode" id="pm-fill"
                    checked={!polishOverwrite} disabled={polishing}
                    onChange={() => setPolishOverwrite(false)} />
                  <label className="form-check-label small" htmlFor="pm-fill">{t('polish_fill_empty')}</label>
                </div>
              </div>
            </div>

            <div className="d-flex flex-wrap gap-3 align-items-end">
              <div>
                <label className="form-label small fw-medium">{t('polish_language')}</label>
                <select className="form-select form-select-sm" style={{ width: 'auto' }}
                  value={polishLang} onChange={e => setPolishLang(e.target.value)}>
                  <option value="nl">Nederlands</option>
                  <option value="en">English</option>
                </select>
              </div>
              <button className="btn btn-primary btn-sm mb-1" onClick={handlePolish}
                disabled={polishing || !(polishFields.situation || polishFields.finding || polishFields.actions)}>
                {polishing
                  ? <><span className="spinner-border spinner-border-sm me-2" />{t('polish_running')}</>
                  : `✨ ${t('polish_run')}`}
              </button>
            </div>

            {/* Per-finding progress */}
            {polishProgress && (
              <div className="mt-3">
                <div className="d-flex align-items-center gap-2 mb-1">
                  <span className="spinner-border spinner-border-sm text-primary flex-shrink-0" />
                  <span className="small text-muted text-truncate">
                    {polishProgress.done + 1}/{polishProgress.total} — {polishProgress.label}
                  </span>
                  {polishProgress.errors > 0 && (
                    <span className="badge bg-warning text-dark ms-auto flex-shrink-0">
                      {polishProgress.errors} fout
                    </span>
                  )}
                </div>
                <div className="progress" style={{ height: 5 }}>
                  <div
                    className="progress-bar"
                    style={{
                      width: `${Math.round((polishProgress.done / polishProgress.total) * 100)}%`,
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
              </div>
            )}

            {polishResult && (
              <div className={`alert py-2 mt-3 mb-0 ${polishResult.errors > 0 ? 'alert-warning' : 'alert-success'}`}>
                {polishResult.polished} {t('polish_done')}
                {polishResult.errors > 0 && ` — ${polishResult.errors} control(s) overgeslagen wegens fout`}
                {polishResult.errors === 0 && ` — ${t('polish_review_hint')}`}
              </div>
            )}
          </div>
        )}

        {/* Sessiemodus: voorstel op basis van de planning */}
        {nowSession && (
          <div className="alert alert-info py-2 d-flex align-items-center gap-2 small">
            <i className="bi bi-clock" />
            <span>{t('session_now')}: <strong>{nowSession.label}</strong> ({nowSession.start_time}–{nowSession.end_time})</span>
            <button className="btn btn-sm btn-primary ms-auto" onClick={() => chooseSession(nowSession.id)}>{t('session_follow')}</button>
          </div>
        )}

        {/* Sessiemodus: actieve sessie */}
        {session && (
          <div className="card mb-3 border-primary">
            <div className="card-body py-2">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span className="badge bg-primary">{session.code || t('plan_kind_session')}</span>
                <strong>{session.title}</strong>
                <span className="text-muted small">
                  {session.day && new Date(`${session.day}T12:00:00Z`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })}
                  {session.start_time && ` · ${session.start_time}–${session.end_time || ''}`}
                  {session.block && ` · ${session.block}`}
                </span>
                <span className="ms-auto small">
                  {sessionAssessed}/{sessionPrimary.length} {t('assessed')}
                  {session.related.length > 0 && <span className="text-muted"> · +{session.related.length} {t('plan_related').toLowerCase()}</span>}
                </span>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => chooseSession(null)} title={t('session_leave')}>
                  <i className="bi bi-x-lg" />
                </button>
              </div>
              <div className="progress mt-2" style={{ height: 4 }}>
                <div className="progress-bar" style={{ width: `${sessionPrimary.length ? (sessionAssessed / sessionPrimary.length) * 100 : 0}%` }} />
              </div>
              {(session.contacts.length > 0 || session.auditee_note) && (
                <div className="small mt-2">
                  <i className="bi bi-people me-1 text-muted" />
                  {session.contacts.map(c => c.name).join(', ')}
                  {session.auditee_note && <span className="text-muted"> {session.contacts.length ? '· ' : ''}<em>{session.auditee_note}</em></span>}
                </div>
              )}
              {(session.topics || session.attention) && (
                <div className="mt-2">
                  <button className="btn btn-link btn-sm p-0 text-decoration-none small" onClick={() => setSessionTopicsOpen(o => !o)}>
                    <i className={`bi bi-chevron-${sessionTopicsOpen ? 'down' : 'right'} me-1`} />{t('plan_topics')}
                  </button>
                  {sessionTopicsOpen && (
                    <div className="row small mt-1">
                      <div className={session.attention ? 'col-md-8' : 'col-12'}>
                        <ul className="mb-0 ps-3" style={{ columns: session.attention ? 1 : 2, columnGap: 32 }}>
                          {(session.topics || '').split('\n').filter(x => x.trim()).map((x, i) => <li key={i}>{x}</li>)}
                        </ul>
                      </div>
                      {session.attention && (
                        <div className="col-md-4">
                          <div className="fw-medium">{t('plan_attention')}</div>
                          <div style={{ whiteSpace: 'pre-line' }}>{session.attention}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="d-flex gap-2 mb-3 align-items-center flex-wrap">
          <select className="form-select form-select-sm" style={{ width: 'auto' }}
            value={section} onChange={e => setSection(e.target.value)}>
            {sections.map(s => <option key={s} value={s}>{s === 'all' ? t('all') : s}</option>)}
          </select>
          <select className="form-select form-select-sm" style={{ width: 'auto' }}
            value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="all">{t('all')}</option>
            {FINDING_STATUSES.map(s => <option key={s} value={s}>{t(`fs_${s}`)}</option>)}
          </select>
          {planSessions.length > 0 && (
            <select className={`form-select form-select-sm ${session ? 'border-primary' : ''}`} style={{ width: 'auto', maxWidth: 320 }}
              value={activeSession || 'all'} onChange={e => chooseSession(e.target.value)}>
              <option value="all">{t('plan_session_filter')}</option>
              {planSessions.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              <option value="none">{t('plan_session_none')}</option>
            </select>
          )}
          <span className="text-muted small">{visible.length} / {total}</span>

        </div>

        {/* Findings table */}
        <div className="card">
          <div className="table-responsive">
            <table className="table table-hover table-clickable table-resizable mb-0">
              <thead className="table-light" onContextMenu={openColMenu}>
                <tr>
                  {tableCols.map((c, i) => {
                    // Greep op elke rand tussen twee kolommen, niet naast de pijlkolom
                    const next = tableCols[i + 1];
                    return (
                      <ResizableTh key={c.key} width={c.key === 'name' ? undefined : colWidth(c.key)}
                        onDrag={next ? (e => dragBorder(e, c.key, next.key, setColWidth)) : undefined}
                        onReset={next ? (() => { setColWidth(c.key, null); setColWidth(next.key, null); }) : undefined}>
                        {c.label}
                      </ResizableTh>
                    );
                  })}
                  <th style={{ width: 52 }} className="text-end">
                    <button className="btn btn-link btn-sm p-0 text-muted" onClick={openColMenu} title={t('col_columns')}>
                      <i className="bi bi-layout-three-columns" />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.map(f => (
                  <tr key={f.id} onClick={() => navigate(`/audits/${id}/findings/${f.id}`)}>
                    {showSection && <td className="text-muted small">{f.section}</td>}
                    <td className="text-muted font-mono small">
                      {f.control_id}
                      {relatedCodes.has(f.control_id) && (
                        <div><span className="badge bg-light text-muted border" style={{ fontSize: 10 }}>{t('plan_related').toLowerCase()}</span></div>
                      )}
                    </td>
                    <td>
                      <div className="fw-medium">{f.title}</div>
                      {showFindingPreview && f.finding && (
                        <div className="text-muted-sm mt-1">
                          ✓ {f.finding.slice(0, 80)}{f.finding.length > 80 ? '…' : ''}
                        </div>
                      )}
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <StatusPicker status={f.status} onChange={s => handleStatusChange(f.id, s)} />
                    </td>
                    {showPriority && (
                      <td className="small">{f.priority ? t(`prio_${f.priority}`) : '—'}</td>
                    )}
                    {showDueDate && (
                      <td className="text-muted small text-nowrap">
                        {f.due_date ? new Date(f.due_date).toLocaleDateString() : '—'}
                      </td>
                    )}
                    {showAssigned && (
                      <td className="text-muted small">{f.assigned_to_name || '—'}</td>
                    )}
                    {showMaturity && (
                      <td className="small">
                        {f.maturity_level != null
                          ? `${f.maturity_level} — ${MATURITY_LABELS[f.maturity_level]}`
                          : <span className="text-muted">—</span>}
                      </td>
                    )}
                    {showNotes && (
                      <td className="text-muted small">
                        {f.notes ? f.notes.slice(0, 60) + (f.notes.length > 60 ? '…' : '') : '—'}
                      </td>
                    )}
                    <td onClick={e => e.stopPropagation()}>
                      <Link to={`/audits/${id}/findings/${f.id}`} className="btn btn-sm btn-outline-secondary">
                        <i className="bi bi-arrow-right" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      {colMenu && (
        <div ref={colMenuRef} className="card shadow" style={{ position: 'fixed', left: colMenu.x, top: colMenu.y, zIndex: 1050, width: 240 }}>
          <div className="card-body p-3">
            <div className="text-muted mb-2" style={{ fontSize: 11 }}>{t('col_columns')}</div>
            {[
              ['showSection',        showSection,        setShowSection,        t('col_section')],
              ['showFindingPreview', showFindingPreview, setShowFindingPreview, t('col_finding_preview')],
              ['showPriority',       showPriority,       setShowPriority,       t('finding_priority')],
              ['showDueDate',        showDueDate,        setShowDueDate,        t('finding_due_date')],
              ['showAssigned',       showAssigned,       setShowAssigned,       t('col_assigned')],
              ['showNotes',          showNotes,          setShowNotes,          t('finding_notes')],
              ...(audit.framework_code === 'iso27001'
                ? [['showMaturity', showMaturity, setShowMaturity, t('maturity')]] : []),
            ].map(([key, val, setter, label]) => (
              <div className="form-check mb-1" key={key}>
                <input className="form-check-input" type="checkbox" id={key}
                  checked={val} onChange={e => setter(e.target.checked)} />
                <label className="form-check-label small" htmlFor={key}>{label}</label>
              </div>
            ))}
            <hr className="my-2" />
            <div className="text-muted mb-2" style={{ fontSize: 11 }}>{t('col_resize_hint')}</div>
            <button className="btn btn-link btn-sm p-0" disabled={!Object.keys(colWidths).length}
              onClick={() => setColWidths({})}>
              {t('col_reset_widths')}
            </button>
          </div>
        </div>
      )}

      {scopeOpen && (
        <ScopeModal
          auditId={id}
          defaultQuestionCount={audit.default_question_count ?? 10}
          frameworkId={audit.framework_id}
          t={t}
          onClose={() => setScopeOpen(false)}
          onSave={result => {
            setScopeOpen(false);
            setScopeResult(result);
            load();
          }}
        />
      )}

      {versionsOpen && (
        <VersionsModal
          auditId={id}
          t={t}
          onClose={() => setVersionsOpen(false)}
          onRestored={(v) => {
            setVersionsOpen(false);
            load();
            alert(t('ver_restored').replace('{v}', v));
          }}
        />
      )}

      {shareOpen && (
        <ShareModal
          auditId={id}
          clientId={audit.client_id}
          t={t}
          onClose={() => setShareOpen(false)}
        />
      )}

      {mailReport && (
        <SendReportMailDialog
          report={mailReport}
          clientId={audit.client_id}
          onClose={() => setMailReport(null)}
        />
      )}

      {previewReport && (
        <ReportPreview
          reportId={previewReport.id}
          filename={previewReport.original_filename}
          onClose={() => setPreviewReport(null)}
        />
      )}

      {/* PDF preview modal */}
      {pdfPreviewUrl && (
        <>
          <div className="modal show d-block" tabIndex="-1" onClick={closePdfPreview}>
            <div
              className="modal-dialog modal-fullscreen-lg-down"
              style={{ maxWidth: '90vw', width: '90vw', margin: '2vh auto' }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-content" style={{ height: '96vh' }}>
                <div className="modal-header py-2">
                  <h6 className="modal-title mb-0">
                    <i className="bi bi-file-pdf me-2 text-danger" />
                    {audit?.name} — PDF Rapport
                  </h6>
                  <div className="d-flex gap-2 ms-auto me-3">
                    <a
                      href={pdfPreviewUrl}
                      download={`audit-rapport-${id}.pdf`}
                      className="btn btn-sm btn-outline-primary"
                    >
                      <i className="bi bi-download me-1" />Downloaden
                    </a>
                  </div>
                  <button type="button" className="btn-close" onClick={closePdfPreview} />
                </div>
                <div className="modal-body p-0" style={{ flex: 1, minHeight: 0 }}>
                  <iframe
                    src={pdfPreviewUrl}
                    title="PDF rapport"
                    style={{ width: '100%', height: '100%', border: 'none' }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={closePdfPreview} />
        </>
      )}

    </>
  );
}
